import { add, differenceInDays, endOfDay, isSameDay, isWithinInterval, startOfDay } from 'date-fns';
import { ProductType, RulesName, RulesType, SalePriceTypes, RecurrenceIntervalsInDays, priceFormats, PaymentMethods } from '../enums';

/**
 * Handle state selection of carousel for addOns screen
 *
 * @param {Object} addOn - addOn selected
 * @param {Object[]} addOnsToShow - state of list of addOns showed in screen
 * @param {requestCallback} setAddOnsToShow - callback for set a new status of addOn selected
 * @param {requestCallback} addProduct - callback for set a addOn in productsToBuy state
 * @param {Boolean} applyApportionment - boolean that indicates when to apply pro rate
 * @returns {void}
 */
export const onSelectAddOnCarouseHandler = (addOn, addOnsToShow, setAddOnsToShow, addProduct, applyApportionment) => {
  // addOnsToShow includes actual status of addOns (if is selected of is blocked)
  const selectedAddOn = addOn;
  const updateSelectedAddOnStatus = addOnsToShow.map(pkg => {
    const addOnCarousel = { ...pkg };
    if (addOnCarousel.productID === selectedAddOn.productID) {
      addOnCarousel.isSelected = !addOnCarousel.isSelected;
    }
    return addOnCarousel;
  });
  const addOnToBlocked = updateSelectedAddOnStatus
    .filter(pkg => pkg.isSelected && pkg.rules?.[RulesType.PURCHASE]?.[RulesName.INCOMPATIBLE_ADDONS]?.length > 0)
    .map(pkg => pkg.rules?.[RulesType.PURCHASE]?.[RulesName.INCOMPATIBLE_ADDONS])
    .flat();
  const updateBlockedStatus = updateSelectedAddOnStatus.map(pkg => {
    const addOnCarousel = { ...pkg };
    addOnCarousel.isBlocked = addOnToBlocked && addOnToBlocked.some(blockedPkg => blockedPkg === addOnCarousel.productID);
    return addOnCarousel;
  });
  // update carousel addOns status
  setAddOnsToShow(updateBlockedStatus);
  // add addOn to productsToBuy state
  addProduct({ ...selectedAddOn, applyApportionment });
}

export const hasAllUrns = (product, user) => {
  const newUrns = product?.urns;
  return (newUrns.every(urn => user?.urn?.includes(urn)));
}

// ################## START - PRO RATE ##################
const getExpirationDate = (mainSubscription, mainSubscriptionProduct, product, user, forceExpiresAt) => {
  if (mainSubscriptionProduct && !hasAllUrns(product, user)) {
    return add(new Date(), { [product.interval.unit + 's']: product?.interval?.amount });
  }
  return mainSubscription.expiresAt;
}

export const getDifferenceInDays = (mainSubscription, date) => {
  if (!date) {
    date = startOfDay(new Date());
  }
  return differenceInDays(endOfDay(new Date(mainSubscription.expiresAt)), date);
}
const getTransactionPaidPrice = (transaction, mainSubscriptionProduct) => {
  const transactionProduct = transaction?.purchasedProducts?.find((prod) =>
    (prod.productId || prod.productID) === mainSubscriptionProduct?.productID
  );
  const hasDiscountApplied = !!transaction?.paymentInfo?.paymentMethod?.couponCode;
  const transactionPrice = Number(hasDiscountApplied ? transaction?.price : transaction?.grossPrice);
  const productTransactionPrice = Number(hasDiscountApplied ? transactionProduct?.price : transactionProduct?.grossPrice);
  const catalogPrice = Number(mainSubscriptionProduct?.userPlanPriceToShow ?? mainSubscriptionProduct?.price);

  if (Number.isFinite(transactionPrice)) {
    return transactionPrice;
  }

  if (Number.isFinite(productTransactionPrice)) {
    return productTransactionPrice;
  }

  return Number.isFinite(catalogPrice) ? catalogPrice : mainSubscriptionProduct?.price;
};
const getPriceByDifferenceInDays = (product, mainSubscription, mainSubscriptionProduct, user, transaction) => {
  let newPrice = product.price;
  const isFreeTrialMainSub = mainSubscription.purchaseState?.freeTrial?.enable;
  const isFakeMainSubscription = mainSubscription.purchaseState?.paymentMethod === PaymentMethods.ACCESS;
  if (mainSubscriptionProduct && (isFreeTrialMainSub || isFakeMainSubscription)) {
    return newPrice;
  }
  const days = RecurrenceIntervalsInDays[product.interval.unit] * product.interval.amount;
  const daysDifference = getDifferenceInDays(mainSubscription);
  
  if (mainSubscriptionProduct && mainSubscription && product.productType === 'plan') {
    const mainSubscriptionDays =
    differenceInDays(endOfDay(new Date(mainSubscription.expiresAt)), startOfDay(new Date(mainSubscription.initiatesAt)));
    if (!hasAllUrns(product, user)) {
      const mainSubscriptionPaidPrice = getTransactionPaidPrice(transaction, mainSubscriptionProduct);
      newPrice = product.priceToShow - mainSubscriptionPaidPrice * (daysDifference / mainSubscriptionDays);
    }
  } else {
    newPrice = (product.priceToShow / days) * daysDifference;
  }
  return newPrice > 0 ? newPrice : 0;
}

export const calculatePriceToExpiresAt = (mainSubscription, checkoutProducts, mainSubscriptionProduct, user, transaction) => {
  return checkoutProducts.map((product) => calculateApportionment(product, mainSubscription, mainSubscriptionProduct, user, transaction));
}

export const addExpirationDate = (product) => {
  return {
    ...product,
    expiresAt: add(new Date(), { [product.interval.unit + 's']: product?.interval?.amount }),
  }
};
export const calculateApportionment = (product, mainSubscription, mainSubscriptionProduct, user, transaction) => {
  const isMainSubscriptioFake = mainSubscription?.purchaseState?.paymentMethod === PaymentMethods.ACCESS;
  const newProduct = { ...product, daysDifference: 0 };
  if (product.price === 0) {
    return newProduct;
  }

  if (!mainSubscription || isMainSubscriptioFake) {
    return newProduct;
  }

  if (!mainSubscriptionProduct && isSameDay(new Date(mainSubscription.expiresAt), new Date(newProduct.expiresAt))) {
    newProduct.expiresAt = getExpirationDate(mainSubscription);
    return newProduct;
  }

  newProduct.grossPrice = product.price;
  newProduct.discountPriceToShow = getPriceByDifferenceInDays(product, mainSubscription, mainSubscriptionProduct, user, transaction);
  newProduct.daysDifference = getDifferenceInDays(mainSubscription);
  newProduct.expiresAt = getExpirationDate(mainSubscription, mainSubscriptionProduct, product, user);
  return newProduct;
};

export const isValidApplyApportionment = (apportionmentConfig) => {
  return (
    apportionmentConfig &&
    typeof (apportionmentConfig?.daysDifference) === 'number' &&
    apportionmentConfig.daysDifference > 0
    // &&
    // apportionmentConfig.daysDifference < APPORTIONMENT_AVERAGE_MONTH_DAYS
  );
};
// ################## END - PRO RATE ##################

// ################## START - SALE PRICE OFFER ##################
export const getSalePrice = (product) => {
  if (!product.salePriceOffers || !Array.isArray(product.salePriceOffers) || product.salePriceOffers.length === 0) {
    return null;
  }

  const now = new Date();
  const salePrice = product.salePriceOffers.find((salePrice) => isWithinInterval(now, {
    start: new Date(salePrice.from),
    end: new Date(salePrice.to)
  }));

  if (salePrice) {
    return salePrice;
  }

  return null;
}

export const calculateFinalPrice = (product, validSalePrice) => {
  let finalPrice;
  if (validSalePrice?.type === SalePriceTypes.OFFER_PRICE) {
    finalPrice = validSalePrice.amount;
  }
  if (validSalePrice?.type === SalePriceTypes.DISCOUNT) {
    finalPrice = calculateDiscount(validSalePrice, product.price);
  }
  return finalPrice;
}

export const calculateDiscount = ({ discountType, amount }, price) => {
  if (discountType === 'percentage') {
    const totalDiscount = price * amount / 100;
    return price - totalDiscount;
  }
  if (discountType === 'fixedAmount') {
    return price - amount;
  }
};

export const applySalePrice = (products) => {
  const newProducts = products.map(product => {
    const newProduct = { ...product };

    if ((product.expiresAt && product.price === 0) || product.grossPrice) {
      return newProduct;
    }

    const salePrice = getSalePrice(product);
    if (salePrice) {
      newProduct.grossPrice = newProduct.grossPrice || newProduct.price;
      newProduct.price = calculateFinalPrice(product, salePrice);
    }
    return newProduct;
  });
  return newProducts;
}
// ################## END - SALE PRICE OFFER ##################

// ################## START - GETTERS FUNCTIONS ##################
/**
 * Format products by tags of products (addOn, premium, streaming, etc...)
 *
 * @param {Object} products - list of all client products
 * @returns {Object[]}
 */
export const formatProductsByType = (products) => {
  const result = products?.reduce((acc, item) => {
    const key = item.productType === ProductType.PLAN
      ? ProductType.PLAN
      : item.metadata?.tags
        ? item.metadata?.tags[item.metadata?.tags.length - 1]
        : item.productType;

    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  return result;
};

export const getProductByProductId = (productId, products) => products.find(product => product.productID === productId);
export const wrappOnlyAddOns = (products) => products?.filter(
  product => product.productType === ProductType.ADD_ON && !(product.metadata?.tags?.includes('hidden'))
) || [];

export const filterHiddenProducts = (queryParamsObject, products) => {
  const { productID, productId, promoSlug } = queryParamsObject || {};
  const explicitProductId = productID || productId;
  const explicitProduct = explicitProductId
    ? products?.find(pro => pro.productID === explicitProductId)
    : undefined;

  if (promoSlug && explicitProductId) {
    return products?.filter(pro => pro.productID === explicitProductId);
  }

  if (explicitProduct && !explicitProduct.hidden) {
    return products?.filter(pro => pro.productID === explicitProductId);
  }

  if (promoSlug) {
    return products;
  }

  return products?.filter(pro => !pro.hidden);
}

export const productWithMostUrnsAndTime = (products) => {
  let productWithMostUrnsAndTime = null
  let urnCounter = 0
  let interval = 0
  for (const product of products) {
    if (product.urns.length > urnCounter ||
      (product.urns.length === urnCounter && RecurrenceIntervalsInDays[product.interval.unit] * product.interval.amount > interval)
    ) {
      urnCounter = product.urns.length
      interval = RecurrenceIntervalsInDays[product.interval.unit] * product.interval.amount
      productWithMostUrnsAndTime = product
    }
  }
  return productWithMostUrnsAndTime
}

export const onlyPlanSubscriptions = (subscriptions, products) => {
  return subscriptions.filter(subscription => {
    const product = products.find(product => product.productID === subscription.productId);
    return product.productType === ProductType.PLAN && subscription;
  });
};

export const getGrossPrice = (availableSalePriceOffer, product) => {
  return availableSalePriceOffer
    ? product?.grossPriceToShow
    : product?.priceToShow || product?.price;
}

export const formatPricesByCountry = (priceToFormat, country) => {
  const format = priceFormats[country];

  if (!format || priceToFormat === undefined || priceToFormat === null) {
    return priceToFormat;
  }

  if (priceToFormat % 1 !== 0 && format.decimalSeparator) {
    priceToFormat = parseFloat(priceToFormat.toFixed(2));
  }

  if (!format.decimalSeparator) {
    priceToFormat = Math.round(priceToFormat);
  } else {
    priceToFormat = Math.round(priceToFormat * 100) / 100
  }

  const parts = priceToFormat.toFixed(format.decimalPlaces).split('.');
  const formattedIntegerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator);

  let formatedNumber = format.decimalSeparator ? formattedIntegerPart + format.decimalSeparator + parts[1] : formattedIntegerPart;

  if (format.currency) {
    formatedNumber = `${format.currency}${formatedNumber}`
  }
  return formatedNumber;
}

export const formattedInvoiceCurrency = (country) => {
  const format = priceFormats[country];
  if (!format) {
    return;
  }
  return format.formattedCurrency;
}

// ################## END - PARSERS FUNCTIONS ##################
